/**
 * The People directory's own presentation table — Task T2-B.
 *
 * `EmployeeDirectoryRow` carries no single `status` field; `listEmployees`'s own
 * description gives the two predicates instead: `active` is
 * `is_active = true AND offboarded_at IS NULL`, and `offboarded` is
 * `offboarded_at IS NOT NULL OR is_active = false`. `employeeStatusOf` is that
 * predicate, transcribed once, so the table cell and the status filter agree on
 * what the two words mean.
 *
 * DESIGN.md §4's icon+text rule applies here exactly as it does to
 * `presence/status.ts`'s own table: a status cell is `Badge`, never colour alone.
 */

import type { MessageKey } from '../i18n/messages';
import type { BadgeTone } from '../states/Badge';
import type { IconName, Severity } from '../states/icons';

export const EMPLOYEE_DISPLAY_STATUSES = ['active', 'offboarded'] as const;
export type EmployeeDisplayStatus = (typeof EMPLOYEE_DISPLAY_STATUSES)[number];

/** `listEmployees`'s own `active` predicate, applied to one row rather than a filter. */
export function employeeStatusOf(row: {
  readonly is_active: boolean;
  readonly offboarded_at: string | null;
}): EmployeeDisplayStatus {
  return row.is_active && row.offboarded_at === null ? 'active' : 'offboarded';
}

export interface EmployeeStatusPresentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
  readonly tone: BadgeTone;
}

/**
 * Active reads `low` (the same severity `presence.ts`'s onsite/remote rows use) —
 * a good, unremarkable fact. Offboarded reads `info`, the same neutral treatment
 * `presence.ts` gives `offline`: leaving is a normal lifecycle fact, not a problem
 * to flag louder.
 *
 * **`active` is painted neutral.** Nearly every row of a company directory is an
 * employee who works there, and a green chip on all of them is a column of green
 * that tells a reader nothing — the one row worth finding is the one that is *not*
 * active, and it can only stand out if the rest are quiet (Luật Màu Có Nghĩa). So
 * `offboarded` keeps its `info` paint and `active` gives its up; both keep the glyph
 * and the word.
 */
export const EMPLOYEE_STATUS_PRESENTATION: Readonly<
  Record<EmployeeDisplayStatus, EmployeeStatusPresentation>
> = {
  active: {
    severity: 'low',
    icon: 'circle-check',
    labelKey: 'people.status.active',
    tone: 'neutral',
  },
  offboarded: {
    severity: 'info',
    icon: 'moon',
    labelKey: 'people.status.offboarded',
    tone: 'severity',
  },
};
